import type { FeatureFlags, MeResponse, StaffRole } from '@ucp/web/lib';
import { COMMERCIAL_SITES_ROLES } from '../commercial/commercialRoles';
import { SURVEY_ROUTE_ROLES } from '../surveys/surveyRoles';

/**
 * Single source of truth for the task-based navigation (PRO-0250 / ADR-014, D4).
 *
 * One `navConfig` array drives BOTH the role-filtered `PrimaryNav` and the
 * per-route `RouteGuard`, and the role-matching logic lives in exactly one
 * place — `hasAnyRole` — which `RequireRole` is also refactored to call (SF-001).
 * That removes the drift risk of duplicating role lists across the nav, the
 * guards and the legacy gate component.
 */

export type NavItemStatus = 'active' | 'placeholder';

export interface NavItem {
  readonly id: string;
  readonly label: string;
  readonly path: string;
  readonly anyOf: StaffRole[];
  /** Exact-match the active route (NavLink `end`). Compose has child routes so it matches by prefix. */
  readonly end?: boolean;
  /**
   * `active` — visible in nav/Home and mounts a real route. `placeholder` — registered
   * for IA continuity but hidden from nav until implemented (no route registered).
   */
  readonly status: NavItemStatus;
  /**
   * Optional feature-flag gate (PRO-0630 SF-002). When set, the item is only
   * surfaced by `navItemsForUser` if the caller's `featureFlags[requiresFlag]` is
   * true. Fail-closed: if no feature flags are supplied the flagged item is hidden.
   * The server (`requireScheduleSendEnabled` + capability) remains authoritative.
   */
  readonly requiresFlag?: keyof FeatureFlags;
}

/**
 * Does the user hold any of `anyOf`? L6 (`schoolId === null`) is platform-wide and
 * satisfies any school-scoped check; a school-scoped role only matches when no
 * `schoolId` is required or the assignment is for that school. This is the one
 * implementation of the rule shared by `PrimaryNav`, `RouteGuard` and `RequireRole`.
 */
/** A workforce-baseline capability that admits a caller with no conferring role (ADR-071). */
export type BaselineCapability = 'policy_read' | 'hr_assistant_ask' | 'marketing_request_submit';

export function hasAnyRole(
  user: Pick<MeResponse, 'roles'> & Partial<Pick<MeResponse, 'workforceBaseline'>>,
  anyOf: readonly StaffRole[],
  schoolId?: string,
  baselineCapability?: BaselineCapability,
): boolean {
  if (
    baselineCapability &&
    user.workforceBaseline?.admitted === true &&
    user.workforceBaseline.pack.includes(baselineCapability)
  ) {
    return true;
  }
  return user.roles.some((assignment) => {
    if (!anyOf.includes(assignment.role)) {
      return false;
    }
    // L6 is platform-wide and satisfies school-scoped checks.
    if (assignment.schoolId === null) {
      return true;
    }
    if (!schoolId) {
      return true;
    }
    return assignment.schoolId === schoolId;
  });
}

const ALL_ROLES: StaffRole[] = ['L1_SENDER', 'L2_APPROVER', 'L6_PLATFORM_ADMIN'];

/**
 * The full target IA (ADR-014, + Amendment 1; + Amendment 5 PRO-0440). Inc-1
 * critical-path areas (Home, Compose, Approvals, Comms settings) mount real
 * surfaces; Monitoring mounts the landed L6 cross-school view (NOT a placeholder,
 * SF-002). The former Templates + Admin Comms task items are consolidated into a
 * single `comms-settings` task at `/settings` (templates/delivery/approvals);
 * platform-estate administration graduated to the portal tier at `/platform/*`.
 * Messages/Inbox remain
 * registered as `placeholder` for roll-forward but are hidden from nav until built.
 * Order is the task-based top-level ordering.
 *
 * PRO-0340: the `operations` item's user-visible label is **Monitoring** to
 * de-collide with the "TAG Operations" portal wordmark; its `id` and `path`
 * (`/ops`) are intentionally kept so deep links, the Home icon map (keyed by
 * `id`) and `titleForPath` derivation need no change (T1).
 */
export const navConfig: NavItem[] = [
  { id: 'home', label: 'Home', path: '/', anyOf: ALL_ROLES, end: true, status: 'active' },
  { id: 'compose', label: 'Compose', path: '/compose', anyOf: ['L1_SENDER'], status: 'active' },
  {
    id: 'approvals',
    label: 'Approvals',
    path: '/approvals',
    anyOf: ['L2_APPROVER'],
    end: true,
    status: 'active',
  },
  {
    id: 'messages',
    label: 'Messages',
    path: '/messages',
    anyOf: ['L1_SENDER', 'L2_APPROVER'],
    end: true,
    status: 'placeholder',
  },
  { id: 'inbox', label: 'Inbox', path: '/inbox', anyOf: ['L1_SENDER'], end: true, status: 'placeholder' },
  {
    id: 'scheduled',
    label: 'Scheduled',
    path: '/scheduled',
    anyOf: ['L1_SENDER'],
    end: true,
    status: 'active',
    requiresFlag: 'scheduleSendEnabled',
  },
  // PRO-3060 / ADR-082 — parent/carer surveys, in the Comms module. Gated to the roles that
  // hold any survey capability + the `surveysEnabled` kill-switch; the server
  // `requireSurveysEnabled` + capability gates stay authoritative. Never L6.
  {
    id: 'surveys',
    label: 'Surveys',
    path: '/surveys',
    anyOf: [...SURVEY_ROUTE_ROLES],
    status: 'active',
    requiresFlag: 'surveysEnabled',
  },
  {
    id: 'comms-settings',
    label: 'Comms settings',
    path: '/settings',
    anyOf: ['L6_PLATFORM_ADMIN'],
    status: 'active',
  },
  // PRO-0490 / ADR-024 (2026-07-13 amendment): the school-owned template-authoring
  // surface. First nav surfacing of the ADR-024 feature — previously deep-link only.
  // Gated to the school authoring roles (L2 added by the amendment) + the existing
  // kill-switch; the server `requireSchoolTemplateAuthoringEnabled` + capability gate
  // stay authoritative. Distinct from the L6-only `comms-settings` (`/settings`).
  {
    id: 'school-templates',
    label: 'School templates',
    path: '/settings/school-templates',
    anyOf: ['L2_APPROVER', 'PRINCIPAL', 'OPS_DIRECTOR'],
    end: true,
    status: 'active',
    requiresFlag: 'schoolTemplateAuthoringEnabled',
  },
  {
    id: 'operations',
    label: 'Monitoring',
    path: '/ops',
    anyOf: ['L6_PLATFORM_ADMIN'],
    end: true,
    status: 'active',
  },
  // Commercial destinations stay in this registry so route titles and the
  // host-of-last-resort path still resolve them. `commsTaskNavItems` keeps them
  // out of the Communications task bar once the Commercial module is mounted.
  //
  // PRO-0820 Sub-slice A / ADR-030/028/044: the Sites (CDP) → Zoho projection-health
  // surface. Gated to the central-functional steward role that actually holds
  // `commercial_sites_projection_manage` (never L6 — EoP) + the
  // `commercialSitesZohoEnabled` kill-switch; the server
  // `requireCommercialSitesZohoEnabled` + capability gates stay authoritative.
  //
  // PRO-1360 MF-SEC-1 — this read `['ADMISSIONS_DIRECTOR']`, the PRO-0820 interim
  // holder retired by PRO-0920 (ADR-029 A3 / TD-1). The route guard and the server
  // capability had both moved to `COMMERCIAL_DATA_STEWARD`, so the item advertised a
  // link every ADMISSIONS_DIRECTOR would be bounced off and hid it from the steward
  // who can use it. Now the single `COMMERCIAL_SITES_ROLES` constant the guard reads.
  {
    id: 'commercial-sites-projection',
    label: 'Sites projection',
    path: '/commercial/sites/projection',
    anyOf: [...COMMERCIAL_SITES_ROLES],
    end: true,
    status: 'active',
    requiresFlag: 'commercialSitesZohoEnabled',
  },
  // PRO-0510 / ADR-029 (+A1/A2): the Commercial Data Platform foundation module root
  // (customers / catalogue / price lists). Gated to the commercial personas (never L6 —
  // EoP) + the `commercialDataEnabled` kill-switch; the CommercialSubNav provides the
  // intra-module tabs. The server gates stay authoritative.
  {
    id: 'commercial-data',
    label: 'Commercial data',
    path: '/commercial/customers',
    anyOf: ['COMMERCIAL_DATA_STEWARD', 'PRICING_MANAGER', 'OPS_DIRECTOR'],
    status: 'active',
    requiresFlag: 'commercialDataEnabled',
  },
  // PRO-1260 Epic 3 / PA-05 §D: school leadership confirms placements. A SEPARATE item
  // from 'commercial-data' because that one lands on /commercial/customers, which needs
  // commercial_data_read — a Principal following it would hit a 403. Same
  // commercialContractsEnabled flag the placements route already rides.
  {
    id: 'commercial-placements',
    label: 'Placements',
    path: '/commercial/placements',
    anyOf: ['PRINCIPAL', 'SCHOOL_BUSINESS_MANAGER', 'PLACEMENT_MANAGER'],
    end: true,
    status: 'active',
    requiresFlag: 'commercialContractsEnabled',
  },
  // PRO-1260 Epic 2 — Closed-Won promotion worklist. Separate from commercial-data so a
  // CONTRACT_MANAGER lands on the worklist (not customers). Sub-flag gated.
  {
    id: 'commercial-won-opportunities',
    label: 'Won opportunities',
    path: '/commercial/won-opportunities',
    anyOf: ['COMMERCIAL_DATA_STEWARD', 'CONTRACT_MANAGER'],
    end: true,
    status: 'active',
    requiresFlag: 'commercialWonPromotionEnabled',
  },
];

/**
 * Nav items the user is entitled to see (deny-by-default: anything they can't reach
 * is hidden). A `requiresFlag` item is additionally hidden unless the corresponding
 * feature flag is true (PRO-0630 SF-002) — fail-closed when `featureFlags` is omitted.
 */
export function navItemsForUser(
  user: Pick<MeResponse, 'roles'>,
  // `Partial` rather than the full shape: the body only reads one optional key at a
  // time, so it is total over a partial, and the module-entitlement predicate in
  // `modulesConfig` can call it without casting a `Partial<FeatureFlags>` back to a
  // full one inside a security-adjacent code path (PRO-1360).
  featureFlags?: Partial<FeatureFlags>,
): NavItem[] {
  return navConfig.filter(
    (item) =>
      item.status === 'active' &&
      hasAnyRole(user, item.anyOf) &&
      (item.requiresFlag === undefined ? true : Boolean(featureFlags?.[item.requiresFlag])),
  );
}
